// Move to Referral Management modal.
//
// Ingests a single uploaded record into Referral Management server-side. The
// user picks the DEPARTMENT / queue this referral should land in. Even though
// the backend keys on `fax_number_id`, we never surface a "fax line" concept —
// targets are labeled by their owner (department / user / organization) and the
// fax number is only shown as secondary text.
//
// Styling matches the Referral Management redesign (Plus Jakarta Sans, purple
// accents, 8px radii) via the `mrm-modal-wrap` wrapClassName, mirroring the
// add-label modal so it looks at home on both the old and new inbox UIs.

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Select,
  Input,
  Form,
  Button,
  Spin,
  Checkbox,
  message,
} from 'antd';
import {
  CaretRightOutlined,
  CaretDownOutlined,
  SwapOutlined,
  CheckCircleFilled,
  FileTextOutlined,
} from '@ant-design/icons';

import ModalWrapper from '../ModalWrapper';
import { formatValidFaxNumber, validFaxNumber } from '../../../global/validation';
import { captureAnalytics } from '../../../actions/preload';
import './moveToReferralManagement.scss';

const { Option } = Select;
const { TextArea } = Input;

const customContentStyle = {
  textAlign: 'left',
};

// Reduce a fax number to its last 10 digits for tolerant comparison.
const normalizeNumber = (number) => (number || '').replace(/\D/g, '').slice(-10);

class MoveToReferralManagement extends Component {
  constructor(props) {
    super(props);
    this.state = {
      targets: [],
      loadingTargets: true,
      targetsError: null,
      selectedFaxNumberId: null,
      showProviderFields: false,
      referringProvider: '',
      referringProviderOffice: '',
      referringProviderFax: '',
      note: '',
      runAi: true,
      isSubmitting: false,
      submitError: null,
      success: false,
    };
  }

  componentDidMount() {
    // Track the manual-referral funnel entry point. No PHI in properties.
    captureAnalytics('upload_move_to_referral_opened');
    this.loadTargets();
  }

  // Best-effort default: preselect the target that matches the user's own
  // (default) fax line so single-department users have near-zero friction.
  getDefaultTarget = (targets) => {
    const { defaultFaxNumber } = this.props;
    if (!defaultFaxNumber) return null;
    const wanted = normalizeNumber(defaultFaxNumber);
    if (!wanted) return null;
    return targets.find((target) => normalizeNumber(target.number) === wanted) || null;
  };

  ownerLabel = (target) => (target.owner_name || target.owner_type || 'Organization');

  loadTargets = async () => {
    this.setState({ loadingTargets: true, targetsError: null });
    const { getReferralIngestionTargets } = this.props;
    const response = await getReferralIngestionTargets();

    if (!response || response.error) {
      this.setState({
        loadingTargets: false,
        targetsError: response?.response?.data?.error
          || 'We couldn\'t load your referral queues. Please try again.',
      });
      return;
    }

    const targets = response.data?.fax_numbers || [];
    let selectedFaxNumberId = null;
    if (targets.length === 1) {
      selectedFaxNumberId = targets[0].id;
    } else {
      const preferred = this.getDefaultTarget(targets);
      if (preferred) selectedFaxNumberId = preferred.id;
    }

    this.setState({ targets, selectedFaxNumberId, loadingTargets: false });
  };

  // Switching targets always resets the AI checkbox to checked — a deliberate
  // uncheck applies only to the target it was made on (ENG-3002, spec B2).
  handleTargetChange = (selectedFaxNumberId) => {
    this.setState({ selectedFaxNumberId, submitError: null, runAi: true });
  };

  handleRunAiChange = () => {
    this.setState((prevState) => {
      const runAi = !prevState.runAi;
      // Toggle-level event: the success event only records the final value
      // after a completed move, so a toggle followed by cancel or a failed
      // submit would otherwise leave no trace. Boolean only, no PHI.
      captureAnalytics('upload_move_to_referral_ai_toggled', { run_ai: runAi });
      return { runAi };
    });
  };

  // The selected target's server-computed eligibility is the ONLY source of
  // truth for offering the opt-out (BCP lines have it without the org flag;
  // derived-labels orgs never get it).
  selectedTarget = () => {
    const { targets, selectedFaxNumberId } = this.state;
    return targets.find((target) => target.id === selectedFaxNumberId) || null;
  };

  aiOptOutAvailable = () => {
    const target = this.selectedTarget();
    return !!(target && target.ai_opt_out_available);
  };

  handleProviderChange = (event) => {
    this.setState({ referringProvider: event.target.value });
  };

  handleProviderOfficeChange = (event) => {
    this.setState({ referringProviderOffice: event.target.value });
  };

  // Light formatting: once 10 digits are present, render them as XXX-XXX-XXXX.
  handleProviderFaxChange = (event) => {
    const raw = event.target.value;
    const digits = raw.replace(/\D/g, '');
    const referringProviderFax = digits.length === 10 ? formatValidFaxNumber(digits) : raw;
    this.setState({ referringProviderFax });
  };

  handleNoteChange = (event) => {
    this.setState({ note: event.target.value });
  };

  toggleProviderFields = () => {
    this.setState((prevState) => ({ showProviderFields: !prevState.showProviderFields }));
  };

  handleSubmit = async () => {
    const {
      selectedFaxNumberId,
      referringProvider,
      referringProviderOffice,
      referringProviderFax,
      note,
      runAi,
    } = this.state;
    const { modalType, moveRecordToReferralManagement } = this.props;

    if (!selectedFaxNumberId) {
      this.setState({ submitError: 'Please choose a referral queue before continuing.' });
      return;
    }

    const record = modalType.data?.record || {};
    const recordId = record.file_token;
    if (!recordId) {
      this.setState({ submitError: 'This record is missing an identifier and can\'t be moved. Please refresh and try again.' });
      return;
    }

    const providerFax = referringProviderFax.trim();
    if (providerFax && !validFaxNumber(providerFax).valid) {
      this.setState({ submitError: 'Please enter a valid 10-digit referring-provider fax number.' });
      return;
    }

    const body = { fax_number_id: selectedFaxNumberId };
    if (referringProvider.trim()) body.referring_provider = referringProvider.trim();
    if (referringProviderOffice.trim()) body.referring_provider_office = referringProviderOffice.trim();
    if (providerFax) body.referring_provider_fax = providerFax;
    if (note.trim()) body.note = note.trim();
    // Send run_ai ONLY when the selected target offered the checkbox — explicit
    // both ways then; omitted otherwise (backend defaults true and enforces
    // eligibility server-side regardless).
    const aiOptOutAvailable = this.aiOptOutAvailable();
    if (aiOptOutAvailable) body.run_ai = runAi;

    this.setState({ isSubmitting: true, submitError: null });
    const response = await moveRecordToReferralManagement(recordId, body);

    if (!response || response.error) {
      this.setState({
        isSubmitting: false,
        submitError: response?.response?.data?.error
          || 'We couldn\'t move this record to Referral Management. Please try again or contact support if the issue persists.',
      });
      return;
    }

    // Funnel completion. Booleans only — never referral/patient content.
    // run_ai is null when the checkbox wasn't offered for the selected target.
    captureAnalytics('upload_move_to_referral_succeeded', {
      has_referring_provider: !!body.referring_provider,
      has_note: !!body.note,
      run_ai: aiOptOutAvailable ? runAi : null,
    });
    message.success({ content: 'Record moved to Referral Management.' });
    this.setState({
      isSubmitting: false,
      success: true,
    });
  };

  // Close the modal (via the real redux hideModal) and SPA-navigate to the
  // referral queue. Navigating remounts the (not-always-realtime) referral list
  // so the new referral shows up. Closing must be explicit — ModalWrapper does
  // not auto-close on the primary action.
  goToReferralManagement = () => {
    const { hideModal } = this.props;
    hideModal();
    this.context.router.history.push('/app/referred');
  };

  // On close after a successful move, reload so the ingested record drops off the
  // uploads list — mirrors the unarchive/clone post-action refresh on this page.
  handleCloseAfterSuccess = () => {
    window.location.reload();
  };

  renderSelectedTarget = (target) => (
    <div className="mrm-target-static">
      <span className="mrm-target-owner">{this.ownerLabel(target)}</span>
      {target.number && (
        <span className="mrm-target-number">
          {formatValidFaxNumber(target.number) || target.number}
        </span>
      )}
    </div>
  );

  renderTargetPicker = () => {
    const {
      targets,
      loadingTargets,
      targetsError,
      selectedFaxNumberId,
    } = this.state;

    if (loadingTargets) {
      return (
        <div className="mrm-loading">
          <Spin size="small" />
          <span>Loading referral queues…</span>
        </div>
      );
    }

    if (targetsError) {
      return (
        <div className="mrm-error">
          <p>{targetsError}</p>
          <Button size="small" onClick={this.loadTargets}>Retry</Button>
        </div>
      );
    }

    if (targets.length === 0) {
      return (
        <p className="mrm-error">
          No referral queues are available for your account. Please contact your
          administrator or Medsender support to set one up.
        </p>
      );
    }

    // Single target: collapse the select into read-only text (near-zero friction).
    if (targets.length === 1) {
      return (
        <div className="mrm-target-single">
          {this.renderSelectedTarget(targets[0])}
        </div>
      );
    }

    return (
      <Select
        className="mrm-select"
        value={selectedFaxNumberId}
        onChange={this.handleTargetChange}
        placeholder="Select a queue or department"
        optionLabelProp="label"
        style={{ width: '100%' }}
      >
        {targets.map((target) => (
          <Option key={target.id} value={target.id} label={this.ownerLabel(target)}>
            <div className="mrm-target-option">
              <span className="mrm-target-owner">{this.ownerLabel(target)}</span>
              {target.number && (
                <span className="mrm-target-number">
                  {formatValidFaxNumber(target.number) || target.number}
                </span>
              )}
            </div>
          </Option>
        ))}
      </Select>
    );
  };

  renderProviderSection = () => {
    const {
      showProviderFields,
      referringProvider,
      referringProviderOffice,
      referringProviderFax,
    } = this.state;

    return (
      <div className="mrm-provider-section">
        <button
          type="button"
          className="mrm-provider-toggle"
          onClick={this.toggleProviderFields}
        >
          {showProviderFields ? <CaretDownOutlined /> : <CaretRightOutlined />}
          <span>Referring provider</span>
          <span className="mrm-optional-pill">Optional</span>
        </button>
        {showProviderFields && (
          <div className="mrm-provider-fields">
            <Form.Item label="Provider name">
              <Input
                placeholder="e.g. Dr. Jane Smith"
                value={referringProvider}
                onChange={this.handleProviderChange}
              />
            </Form.Item>
            <Form.Item label="Office / practice">
              <Input
                placeholder="e.g. Lakeview Dermatology"
                value={referringProviderOffice}
                onChange={this.handleProviderOfficeChange}
              />
            </Form.Item>
            <Form.Item
              label="Fax number"
              help="Adding the provider's fax lets referral replies go back to them automatically."
            >
              <Input
                placeholder="XXX-XXX-XXXX"
                value={referringProviderFax}
                onChange={this.handleProviderFaxChange}
              />
            </Form.Item>
          </div>
        )}
      </div>
    );
  };

  // AI extraction opt-out (ENG-3002). Rendered only when the CURRENTLY SELECTED
  // target is eligible per the server-computed ai_opt_out_available boolean.
  // The helper text is a required one-way disclosure: extraction can't be run
  // later on the resulting referral (Decision 14).
  renderAiSection = () => {
    const { runAi } = this.state;
    if (!this.aiOptOutAvailable()) return null;

    return (
      <section className="mrm-section">
        <Checkbox checked={runAi} onChange={this.handleRunAiChange}>
          Extract patient details with AI
        </Checkbox>
        <p className="mrm-ai-helper">
          Automatically fills patient name and date of birth from the document.
          This can&apos;t be run later on the referral.
        </p>
      </section>
    );
  };

  renderForm = () => {
    const { modalType } = this.props;
    const { note, submitError } = this.state;
    const record = modalType.data?.record || {};
    const patientName = record.patient_name || record.document_title;

    return (
      <div className="mrm-modal">
        <p className="mrm-intro">
          Send this uploaded document straight into Referral Management — no need
          to fax it to yourself. We&apos;ll drop it into the queue you choose so
          your team can start working it right away.
        </p>

        {patientName && (
          <div className="mrm-doc-line">
            <FileTextOutlined />
            <span>
              Moving
              {' '}
              <span className="mrm-doc-line__name">{patientName}</span>
            </span>
          </div>
        )}

        <Form layout="vertical" className="mrm-form">
          <section className="mrm-section">
            <div className="mrm-section__head">
              <span className="mrm-section__title">Referral queue / department</span>
            </div>
            {this.renderTargetPicker()}
          </section>

          <section className="mrm-section">
            {this.renderProviderSection()}
          </section>

          {this.renderAiSection()}

          <section className="mrm-section">
            <div className="mrm-section__head">
              <span className="mrm-section__title">Note</span>
              <span className="mrm-optional-pill">Optional</span>
            </div>
            <TextArea
              className="mrm-textarea"
              placeholder="Add context for the team working this referral"
              value={note}
              onChange={this.handleNoteChange}
              autoSize={{ minRows: 2, maxRows: 4 }}
            />
          </section>

          {submitError && (
            <p className="mrm-submit-error" role="alert">{submitError}</p>
          )}
        </Form>
      </div>
    );
  };

  renderSuccess = () => (
    <div className="mrm-success">
      <span className="mrm-success__icon">
        <CheckCircleFilled />
      </span>
      <h3 className="mrm-success__title">You&apos;re all set</h3>
      <p className="mrm-success__body">
        This document is now in Referral Management. Open the queue to start
        working it, or head back to your uploads.
      </p>
    </div>
  );

  render() {
    const { hideModal } = this.props;
    const {
      loadingTargets,
      targetsError,
      targets,
      selectedFaxNumberId,
      isSubmitting,
      success,
    } = this.state;

    if (success) {
      return (
        <ModalWrapper
          hideModal={this.handleCloseAfterSuccess}
          customContentStyle={customContentStyle}
          wrapClassName="mrm-modal-wrap"
          modalTitle="Moved to Referral Management"
          action={this.goToReferralManagement}
          actionName="View in Referral Management"
          dismiss="Done"
        >
          {this.renderSuccess()}
        </ModalWrapper>
      );
    }

    const canSubmit = !loadingTargets
      && !targetsError
      && targets.length > 0
      && !!selectedFaxNumberId;

    return (
      <ModalWrapper
        hideModal={hideModal}
        customContentStyle={customContentStyle}
        wrapClassName="mrm-modal-wrap"
        modalTitle={(
          <span className="mrm-modal-title">
            <SwapOutlined />
            Move to Referral Management
          </span>
        )}
        action={this.handleSubmit}
        actionName="Move to Referral Management"
        dismiss="Cancel"
        disabled={!canSubmit || isSubmitting}
        isLoading={isSubmitting}
      >
        {this.renderForm()}
      </ModalWrapper>
    );
  }
}

MoveToReferralManagement.contextTypes = {
  router: PropTypes.object.isRequired,
};

MoveToReferralManagement.defaultProps = {
  defaultFaxNumber: null,
};

MoveToReferralManagement.propTypes = {
  hideModal: PropTypes.func.isRequired,
  getReferralIngestionTargets: PropTypes.func.isRequired,
  moveRecordToReferralManagement: PropTypes.func.isRequired,
  defaultFaxNumber: PropTypes.string,
  modalType: PropTypes.shape({
    type: PropTypes.string,
    data: PropTypes.shape({
      record: PropTypes.shape({
        file_token: PropTypes.string,
        patient_name: PropTypes.string,
        document_title: PropTypes.string,
      }),
    }),
  }).isRequired,
};

export default MoveToReferralManagement;
